import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/** Default per-request budget. Kept slightly above the server's 10s upstream
 * timeout so a clean 5xx from our proxy wins over a client-side abort. */
const DEFAULT_TIMEOUT_MS = 12_000;

export type AbortableResource<T> = {
	data: T | null;
	isLoading: boolean;
	/** Real failures only (timeout / 5xx / network) — never an intentional abort. */
	error: Error | null;
	refetch: () => void;
	enabled: boolean;
};

/**
 * Shared fetching strategy for the therapist-scheduling hooks.
 *
 * - Threads an AbortSignal to the fetcher and aborts the previous request when
 *   params change (re-run) or the component unmounts — so slow responses can't
 *   land on stale state, and superseded requests are actually cancelled (not
 *   just ignored), sparing the upstream load.
 * - Adds a client-side timeout so a hung upstream surfaces an error instead of
 *   spinning forever.
 * - Distinguishes an intentional abort (supersede/unmount) from a real failure:
 *   the former is swallowed, the latter is exposed with `refetch` for retry.
 *
 * `key` is the serialized param signature; the request re-runs only when it (or
 * `enabled`) changes. `fetcher` is read through a ref so it may close over fresh
 * params without retriggering the effect.
 */
export function useAbortableResource<T>(
	fetcher: (signal: AbortSignal) => Promise<T>,
	{
		enabled,
		key,
		timeoutMs = DEFAULT_TIMEOUT_MS,
	}: { enabled: boolean; key: string; timeoutMs?: number },
): AbortableResource<T> {
	const { t } = useTranslation("appointments-form", {
		keyPrefix: "appt_schedule.schedule_picker.error",
	});
	const [data, setData] = useState<T | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<Error | null>(null);
	const controllerRef = useRef<AbortController | null>(null);
	const fetcherRef = useRef(fetcher);
	fetcherRef.current = fetcher;

	// biome-ignore lint/correctness/useExhaustiveDependencies: keyed on `key` (serialized params); fetcher read via ref
	const run = useCallback(() => {
		if (!enabled) return;
		controllerRef.current?.abort();
		const controller = new AbortController();
		controllerRef.current = controller;
		const timeout = setTimeout(
			() =>
				controller.abort(new DOMException("Request timed out", "TimeoutError")),
			timeoutMs,
		);

		setIsLoading(true);
		setError(null);
		fetcherRef
			.current(controller.signal)
			.then((res) => {
				if (!controller.signal.aborted) {
					setData(res);
					setError(null);
				}
			})
			.catch((e: Error) => {
				// Intentional abort (superseded by a newer request or unmounted):
				// swallow. A timeout aborts too, but with a TimeoutError we surface.
				if (controller.signal.aborted && e.name !== "TimeoutError") return;
				setError(e.name === "TimeoutError" ? new Error(t("timeout")) : e);
			})
			.finally(() => {
				clearTimeout(timeout);
				if (controllerRef.current === controller) setIsLoading(false);
			});
	}, [enabled, key, timeoutMs, t]);

	useEffect(() => {
		run();
		return () => controllerRef.current?.abort();
	}, [run]);

	return { data, isLoading, error, refetch: run, enabled };
}
